// Shared role-permission helpers (cross-cutting consistency pass — web review
// found write actions in several screens with no client-side role gating, even
// though the server already enforces roles correctly on every write endpoint via
// services/api's `requireRole` middleware). These hooks exist purely as
// defense-in-depth + UX: a READ_ONLY or wrong-department user should see a
// disabled button with an explanatory hint instead of an enabled one that would
// 403 on click. Mirrors features/settings/permissions.ts's `useIsAdmin` (the
// settings feature's own, narrower version of this same idea) — same source
// (`useAuth().activeTenant?.role`), same "server is still the source of truth"
// caveat applies here.
import { useAuth } from './AuthContext';

/** READ_ONLY — the one role with no write endpoints anywhere in the API. */
export function useIsReadOnly(): boolean {
  const auth = useAuth();
  return auth.activeTenant?.role === 'READ_ONLY';
}

/** OPERATIONS or ADMIN — matches `requireRole('OPERATIONS', 'ADMIN')`, the gate on
 * every purchasing-intake, production/pack-conversion, silo-counts and
 * stores-transfers write endpoint (goods receipts, tickets, purchase orders,
 * supplier/contract masters, runs, pack conversions, silo readings/counts,
 * stores issues/transfers — create, post, close, complete, reverse, cancel). */
export function useCanOperate(): boolean {
  const auth = useAuth();
  const role = auth.activeTenant?.role;
  return role === 'OPERATIONS' || role === 'ADMIN';
}

/** SALES_ACCOUNTS or ADMIN — matches `requireRole('SALES_ACCOUNTS', 'ADMIN')`, the
 * gate on invoice approve/void and `POST /invoicing/raise`. Dispatch post is
 * wider (`requireRole('OPERATIONS', 'SALES_ACCOUNTS', 'ADMIN')`) — combine with
 * `useCanOperate()` at the call site for that one. */
export function useCanSell(): boolean {
  const auth = useAuth();
  const role = auth.activeTenant?.role;
  return role === 'SALES_ACCOUNTS' || role === 'ADMIN';
}

/** ADMIN only — matches `requireRole('ADMIN')`, the gate on config-only writes
 * (recipe/stage config, silo calibration, settings) that no operational role may
 * touch. */
export function useIsAdmin(): boolean {
  const auth = useAuth();
  return auth.activeTenant?.role === 'ADMIN';
}
